import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useDashboardContext } from '../../api/useDashboardContext';
import { usePredictiveContext } from '../../api/usePredictiveContext';
import { useValidationContext } from '../../api/useValidationContext';
import {
  buildSectionPath,
  normalizeSectionSlug,
} from '../../dashboard/utils/sectionRouting.js';
import { ALL_ALLOWED_LABEL as ALL_MY_AGENCIES_LABEL } from '../../dashboard/config/filterData.js';

function normalizeNavHref(href) {
  if (typeof href !== 'string') {
    return href;
  }

  const trimmed = href.trim();
  if (!trimmed) {
    return '/dashboard';
  }

  if (!trimmed.startsWith('#')) {
    return trimmed;
  }

  const fragment = trimmed.slice(1).split('?')[0];
  const segments = fragment.split('/').filter(Boolean);
  if (segments.length === 0) {
    return '/dashboard';
  }

  const slug = normalizeSectionSlug(segments[segments.length - 1]);
  if (slug) {
    return buildSectionPath(slug);
  }

  const sanitized = fragment.replace(/^\/+/, '');
  if (!sanitized) {
    return '/dashboard';
  }

  if (sanitized.startsWith('dashboard/')) {
    return `/${sanitized}`;
  }

  return `/dashboard/${sanitized}`;
}

function Sidebar({ user }) {
  const countyId = user?.county?.id || user?.countyId || user?.county_id || null;

  const { data: dashboardContext } = useDashboardContext(
    countyId ? { countyId } : {},
    { skip: !user }
  );

  const { data: predictiveContext } = usePredictiveContext(
    countyId ? { countyId } : undefined,
    { skip: !user }
  );

  const { data: validationContext } = useValidationContext(
    countyId ? { countyId } : undefined,
    { skip: !user }
  );

  const predictiveNav = useMemo(() => {
    const predictiveSection =
      predictiveContext?.navigation?.find((section) => section.id === 'cohort_insights') ||
      dashboardContext?.navigation?.find((section) => section.id === 'predictive') ||
      null;

    const items =
      predictiveSection?.items
        ?.filter((item) => item && item.href)
        .map((item) => ({
          id: item.id || item.href,
          label: item.label,
          href: item.href,
          disabled: Boolean(item.disabled),
        })) || [];

    const disabled =
      Boolean(predictiveSection?.disabled) ||
      (predictiveContext?.permissions
        ? !predictiveContext.permissions.hasPredictiveAccess
        : dashboardContext?.permissions
          ? !dashboardContext.permissions.hasPredictiveAccess
          : false);

    const label =
      predictiveSection?.label ||
      predictiveContext?.meta?.title ||
      'Predictive Analytics';

    return {
      label,
      items,
      disabled,
    };
  }, [
    dashboardContext?.navigation,
    dashboardContext?.permissions,
    predictiveContext?.navigation,
    predictiveContext?.permissions,
    predictiveContext?.meta?.title,
  ]);

  const validationNav = useMemo(() => {
    const validationSection =
      validationContext?.navigation?.find((section) => section.id === 'upload') || null;

    const items =
      validationSection?.items
        ?.filter((item) => item && item.href)
        .map((item) => ({
          id: item.id || item.href,
          label: item.label,
          href: item.href,
          disabled: Boolean(item.disabled),
        })) || [];

    const disabled =
      Boolean(validationSection?.disabled) ||
      (validationContext?.permissions ? !validationContext.permissions.hasUploadAccess : false);

    const label =
      validationSection?.label ||
      validationContext?.meta?.title ||
      'Validate Data';

    return {
      label,
      items,
      disabled,
    };
  }, [
    validationContext?.navigation,
    validationContext?.permissions,
    validationContext?.meta?.title,
  ]);

  // Use dashboard context navigation if available, otherwise fall back to basic structure
  const navSections = useMemo(() => {
    if (dashboardContext?.navigation && Array.isArray(dashboardContext.navigation)) {
      return dashboardContext.navigation;
    }
    // Fallback to basic structure
    return [];
  }, [dashboardContext?.navigation]);

  const organizationLabel = useMemo(() => {
    const scope = dashboardContext?.agencyAccess;
    if (scope?.primaryPartnerName) {
      return scope.primaryPartnerName;
    }

    const partnerNames = scope?.allowedPartners
      ?.map((partner) => partner?.partner_name)
      .filter(Boolean) || [];

    if (partnerNames.length === 1) {
      return partnerNames[0];
    }

    if (partnerNames.length > 1) {
      return ALL_MY_AGENCIES_LABEL;
    }

    return (user?.county?.name || 'Alameda') + ' County';
  }, [dashboardContext?.agencyAccess, user?.county?.name]);

  const renderNavLink = (item, { includeIcon = true } = {}) => {
    const to = normalizeNavHref(item.href);

    const getClassName = ({ isActive }) => {
      const classes = [];
      if (isActive) {
        classes.push('active');
      }
      if (item.disabled) {
        classes.push('disabled');
      }
      return classes.join(' ');
    };

    const handleClick = (event) => {
      if (item.disabled) {
        event.preventDefault();
      }
    };

    return (
      <NavLink
        to={to}
        className={getClassName}
        onClick={handleClick}
        aria-disabled={item.disabled}
        tabIndex={item.disabled ? -1 : undefined}
      >
        {includeIcon && item.icon ? <i className={item.icon}></i> : null}
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className="main-sidebar">
      <section className="sidebar">
        <div className="sidebar-header">
          <h1 className="logo-lg">Mobius</h1>
          <h2 className="sidebar-county-name">{organizationLabel}</h2>
        </div>

        <ul className="sidebar-menu">
          {navSections.map((section) => (
            <React.Fragment key={section.id}>
              <li className="header">{section.label?.toUpperCase() || section.id?.toUpperCase()}</li>

              {section.items?.map((item) => (
                <li key={item.id || item.href}>
                  {renderNavLink(item, { includeIcon: false })}
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>
      </section>
    </aside>
  );
}

export default Sidebar;
